import {
  useMemo,
  useState,
} from "react";

import {
  matchesTutorialSearch,
  normalizeTutorialText,
} from "../utils/tutorialSearchUtils";

export default function useTutorialSearch({
  tutorial,
  sections = [],
  steps = {},
  faqs = [],
}) {
  const [query, setQuery] =
    useState("");

  const results = useMemo(() => {
    const normalizedQuery =
      normalizeTutorialText(query);

    const hasQuery =
      Boolean(normalizedQuery);

    /*
     * Recherche dans l'introduction.
     */
    const introMatches =
      !hasQuery ||
      matchesTutorialSearch(
        [
          tutorial?.title,
          tutorial?.subtitle,
          tutorial?.intro?.label,
          tutorial?.intro?.title,
          tutorial?.intro?.content,
        ],
        normalizedQuery
      );

    const filteredSteps = {};
    const filteredSections = [];

    sections.forEach((section) => {
      /*
       * L'introduction est traitée
       * séparément.
       */
      if (section.id === "intro") {
        if (introMatches) {
          filteredSections.push(
            section
          );
        }

        return;
      }

      const sectionSteps =
        Array.isArray(
          steps[section.id]
        )
          ? steps[section.id]
          : [];

      const sectionMatches =
        matchesTutorialSearch(
          [
            section.id,
            section.label,
          ],
          normalizedQuery
        );

      /*
       * Si le titre de la section
       * correspond, toutes ses étapes
       * restent affichées.
       */
      const matchingSteps =
        !hasQuery || sectionMatches
          ? sectionSteps
          : sectionSteps.filter(
              (step) =>
                matchesTutorialSearch(
                  [
                    step.title,
                    step.text,
                    step.keywords,
                    step.slug,

                    ...(step.medias || []).map(
                      (media) =>
                        [
                          media.alt,
                          media.caption,
                        ].join(" ")
                    ),
                  ],
                  normalizedQuery
                )
            );

      if (
        matchingSteps.length > 0 ||
        sectionMatches
      ) {
        filteredSections.push(
          section
        );

        filteredSteps[section.id] =
          matchingSteps;
      }
    });

    const filteredFaqs =
      !hasQuery
        ? faqs
        : faqs.filter((faq) =>
            matchesTutorialSearch(
              [
                faq.q,
                faq.a,
                faq.category,
                faq.keywords,
                faq.slug,
              ],
              normalizedQuery
            )
          );

    const stepCount =
      Object.values(
        filteredSteps
      ).reduce(
        (total, rows) =>
          total + rows.length,
        0
      );

    const totalResults =
      stepCount +
      filteredFaqs.length +
      (hasQuery && introMatches
        ? 1
        : 0);

    return {
      hasQuery,
      normalizedQuery,
      introMatches,
      filteredSections,
      filteredSteps,
      filteredFaqs,
      stepCount,
      faqCount:
        filteredFaqs.length,
      totalResults,

      hasResults:
        introMatches ||
        stepCount > 0 ||
        filteredFaqs.length > 0,
    };
  }, [
    query,
    tutorial,
    sections,
    steps,
    faqs,
  ]);

  const clearSearch = () => {
    setQuery("");
  };

  return {
    query,
    setQuery,
    clearSearch,
    ...results,
  };
}